/**
 * Collections — the CONTROL surface. What exists, what is live, and one click into any single
 * collection.
 *
 * Evan (2026-08-23): "for collections, can we have a page in the admin dash where I can choose
 * from options for cover photo and name, and enter name / edit manually if I want. and can I see
 * which all of the collections are live at a given time and change what's live in real time?"
 * Evan (2026-08-24): "i need to be able to expand view of a collection and really get in the
 * weeds of editing it as I want."
 *
 * This file stays the TABLE. The weeds live in `components/CollectionDetailPanel.tsx`, opened by
 * the "Open" link on every row — venue-by-venue evidence, pin / exclude, rule editing with a live
 * count, and per-city coverage. Splitting them keeps this page answering the question it opens
 * on ("what is live right now") in one screenful.
 *
 * ─── WRITE PATH ──────────────────────────────────────────────────────────────────────────────
 * `public.search_guides` has RLS ENABLED and ZERO policies (20260855000000), so a direct
 * `from('search_guides').update(...)` from this dashboard matches no rows and silently succeeds
 * with nothing changed — the same trap `VenueEditModal` documents for `venues` and
 * `CityHeroPickerModal` documents for `cities`. Every write here therefore goes through a
 * SECURITY DEFINER `admin_*` RPC that asserts `is_admin()` server-side. `canEdit` below only
 * decides whether to DRAW a control; it is never what enforces anything.
 *
 * ─── STATE OF PLAY, VERIFIED ON PRODUCTION 2026-08-24 ────────────────────────────────────────
 * 20260871000000 IS NOW APPLIED, so the city-aware `admin_list_search_guides(p_city_id)`, the
 * cover RPCs and `admin_guide_preview_cities()` are all live. The pre-migration fallbacks below
 * are kept anyway — they cost nothing and they are what makes a not-yet-applied migration a quiet
 * set-up note rather than a red banner.
 *
 * 38 collections exist and 18 are live. 742 venues carry a rating; 402 of those carry a photo and
 * so can appear in a rail at all; 190 carry any thought tag. 28 cities clear the 4-venue
 * photographed-and-rated pool that makes them worth previewing.
 *
 * ─── ⚠️ THREE ROWS CANNOT BE EDITED FROM THIS PAGE TODAY ─────────────────────────────────────
 * "Underground", "Uptown" and "Downtown" are `kind = 'underground'` / `'neighborhood'`. Both kinds
 * are live in `search_guide_venue_ids` (20260870000000) but were never added to the whitelist in
 * `admin_upsert_search_guide`, so Rename — and the live/off switch's fallback path — raise
 * `unknown kind underground`. §8 of `supabase/migrations/20260878000000_collection_detail_editing.sql`
 * fixes it; until that is applied, the detail panel says so instead of echoing the raw error.
 *
 * ─── WHY THERE IS A "PREVIEW CITY" SELECTOR ──────────────────────────────────────────────────
 * Every live collection except the two seeded neighbourhoods has `city_id IS NULL`, which for a
 * city-scoped kind means "follow the READER's city". "A night out in {city}" is ONE row that
 * renders a different list in each of the 28 previewable cities, so it has no size and no cover
 * until you name a city.
 *
 * The same fact governs covers: PINNING A COVER ON A CITY-SCOPED COLLECTION ONLY AFFECTS READERS
 * IN THAT VENUE'S CITY. Everyone else keeps the automatic pick. That is not a limitation of this
 * page, it is what a per-reader collection is, and the row says so rather than pretending.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { CollectionCoverPickerModal } from '../components/CollectionCoverPickerModal';
import { CollectionDetailPanel } from '../components/CollectionDetailPanel';
import {
  GLOBAL_KINDS,
  KIND_LABELS,
  MIN_VENUES,
  VIEWER_KINDS,
  errText,
  rpcMissing,
  type CoverOption,
  type GuideRow,
} from '../lib/collectionKinds';

type PreviewCity = { id: string; name: string; pool: number };

const PREVIEW_CITY_STORAGE_KEY = 'admin:collections:previewCity';

function readStoredPreviewCity(): string {
  try {
    return window.localStorage.getItem(PREVIEW_CITY_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeStoredPreviewCity(id: string): void {
  try {
    if (id) window.localStorage.setItem(PREVIEW_CITY_STORAGE_KEY, id);
    else window.localStorage.removeItem(PREVIEW_CITY_STORAGE_KEY);
  } catch {
    /* private window / blocked site data — the selector still works for this session */
  }
}

function normalizeGuides(raw: unknown): GuideRow[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Partial<GuideRow>[]).map((g) => ({
    id: String(g.id ?? ''),
    title: g.title ?? '',
    subtitle: g.subtitle ?? null,
    kind: g.kind ?? 'manual',
    city_id: g.city_id ?? null,
    city_name: g.city_name ?? null,
    tag: g.tag ?? null,
    categories: g.categories ?? null,
    venue_ids: g.venue_ids ?? null,
    sort_order: typeof g.sort_order === 'number' ? g.sort_order : 100,
    is_active: Boolean(g.is_active),
    venue_count: typeof g.venue_count === 'number' ? g.venue_count : Number(g.venue_count ?? 0) || 0,
    scope_city_id: g.scope_city_id ?? null,
    cover_venue_id: g.cover_venue_id ?? null,
    cover_venue_name: g.cover_venue_name ?? null,
    cover_photo_url: g.cover_photo_url ?? null,
    cover_in_scope: Boolean(g.cover_in_scope),
    effective_cover_venue_id: g.effective_cover_venue_id ?? null,
    effective_cover_venue_name: g.effective_cover_venue_name ?? null,
    effective_cover_photo_url: g.effective_cover_photo_url ?? null,
    renders: g.renders,
  }));
}

export function CollectionsScreen({ canEdit = true }: { canEdit?: boolean }) {
  const [rows, setRows] = useState<GuideRow[]>([]);
  const [cities, setCities] = useState<PreviewCity[]>([]);
  const [previewCityId, setPreviewCityId] = useState<string>(readStoredPreviewCity);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  const [coverSupported, setCoverSupported] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftSubtitle, setDraftSubtitle] = useState('');
  const [coverGuideId, setCoverGuideId] = useState<string | null>(null);
  const [detailGuideId, setDetailGuideId] = useState<string | null>(null);

  const { showError, showSuccess, toastNode } = useToast();

  /**
   * Preview cities. Pre-migration `admin_guide_preview_cities` is absent, so fall back to a plain
   * `cities` read — RLS on `cities` is one permissive public SELECT policy (`cities_read_public`),
   * documented in `CityHeroPickerModal`'s header — which keeps the selector working before the
   * migration even though the counts behind it will not be.
   */
  const loadCities = useCallback(async () => {
    const { data, error: rpcErr } = await supabase.rpc('admin_guide_preview_cities');
    if (!rpcErr && Array.isArray(data)) return data as PreviewCity[];
    /* `total_shado_ratings` is one of the additive columns `CityOverviews` guards against being
       absent — PostgREST errors the whole select if it is, so fall back once more to bare names. */
    let fallback: { id: string; name: string | null }[] | null = null;
    const ranked = await supabase
      .from('cities')
      .select('id, name, total_shado_ratings')
      .order('total_shado_ratings', { ascending: false, nullsFirst: false })
      .limit(60);
    if (ranked.error) {
      const bare = await supabase.from('cities').select('id, name').order('name').limit(60);
      if (bare.error) throw new Error(bare.error.message);
      fallback = (bare.data ?? []) as { id: string; name: string | null }[];
    } else {
      fallback = (ranked.data ?? []) as { id: string; name: string | null }[];
    }
    return (fallback ?? [])
      .filter((c) => c.id && c.name)
      .map((c) => ({ id: c.id, name: c.name as string, pool: 0 }));
  }, []);

  /**
   * `requestedCityId` is a PARAMETER rather than a captured piece of state on purpose. `load` is
   * memoised on `[loadCities]` so it survives every re-render — which means a closure over
   * `previewCityId` would freeze the city as of first render and the Refresh button would quietly
   * re-fetch the city you were looking at ten minutes ago.
   */
  const load = useCallback(async (requestedCityId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const cityList = await loadCities();
      setCities(cityList);

      /* Keep the requested/stored city only if it is still offered; otherwise take the richest. */
      let cityId = requestedCityId ?? readStoredPreviewCity();
      if (!cityId || !cityList.some((c) => c.id === cityId)) {
        cityId = cityList[0]?.id ?? '';
        setPreviewCityId(cityId);
        writeStoredPreviewCity(cityId);
      }

      let guides: unknown = null;
      let note: string | null = null;
      let covers = true;

      const withCity = await supabase.rpc('admin_list_search_guides', {
        p_city_id: cityId || null,
      });
      if (withCity.error) {
        if (rpcMissing(withCity.error.message, 'admin_list_search_guides')) {
          /* Pre-migration signature: no arguments, and therefore no city and no cover fields. */
          const bare = await supabase.rpc('admin_list_search_guides', {});
          if (bare.error) throw new Error(bare.error.message);
          guides = bare.data;
          covers = false;
          note =
            'Covers and per-city venue counts are not set up yet. In Supabase → SQL, apply ' +
            'supabase/migrations/20260871000000_collection_admin_covers.sql, then reload. Until then ' +
            'every collection that follows the reader’s city reports 0 venues — that is the old ' +
            'function answering without a city, not an empty collection. Renaming and the live/off ' +
            'switch already work.';
        } else {
          throw new Error(withCity.error.message);
        }
      } else {
        guides = withCity.data;
      }

      setRows(normalizeGuides(guides));
      setCoverSupported(covers);
      setSetupNote(note);
    } catch (e: unknown) {
      setError(errText(e, 'Could not load collections'));
    } finally {
      setLoading(false);
    }
  }, [loadCities]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Re-list for one city without re-fetching the city list. */
  const reloadForCity = useCallback(
    async (cityId: string) => {
      setPreviewCityId(cityId);
      writeStoredPreviewCity(cityId);
      if (!coverSupported) return; /* the pre-migration function ignores the city anyway */
      setLoading(true);
      try {
        const { data, error: rpcErr } = await supabase.rpc('admin_list_search_guides', {
          p_city_id: cityId || null,
        });
        if (rpcErr) throw new Error(rpcErr.message);
        setRows(normalizeGuides(data));
      } catch (e: unknown) {
        showError(errText(e, 'Could not reload for that city'));
      } finally {
        setLoading(false);
      }
    },
    [coverSupported, showError],
  );

  /**
   * Live / off, applied immediately.
   *
   * Preferred path is the dedicated setter, which touches `is_active` and nothing else. The
   * fallback re-sends every field through the existing 10-arg `admin_upsert_search_guide` so the
   * switch works before the migration — but it is strictly worse, because that RPC re-validates
   * kind / tag / categories / venue_ids and a legacy row that no longer satisfies today's rules
   * would refuse to switch OFF. Hence the dedicated setter existing at all.
   */
  const toggleLive = useCallback(
    async (g: GuideRow, next: boolean) => {
      setBusyId(g.id);
      try {
        const { error: rpcErr } = await supabase.rpc('admin_set_search_guide_active', {
          p_id: g.id,
          p_is_active: next,
        });
        if (rpcErr) {
          if (!rpcMissing(rpcErr.message, 'admin_set_search_guide_active')) throw new Error(rpcErr.message);
          const { error: upsertErr } = await supabase.rpc('admin_upsert_search_guide', {
            p_id: g.id,
            p_title: g.title,
            p_subtitle: g.subtitle,
            p_kind: g.kind,
            p_city_id: g.city_id,
            p_tag: g.tag,
            p_categories: g.categories,
            p_venue_ids: g.venue_ids,
            p_sort_order: g.sort_order,
            p_is_active: next,
          });
          if (upsertErr) throw new Error(upsertErr.message);
        }
        setRows((prev) => prev.map((r) => (r.id === g.id ? { ...r, is_active: next } : r)));
        showSuccess(next ? `"${g.title}" is live` : `"${g.title}" is off`);
      } catch (e: unknown) {
        showError(errText(e, 'Could not change what is live'));
      } finally {
        setBusyId(null);
      }
    },
    [showError, showSuccess],
  );

  /** Rename by hand. Goes through the existing 10-arg upsert — no new RPC, works today. */
  const saveTitle = useCallback(
    async (g: GuideRow) => {
      const title = draftTitle.trim();
      if (!title) {
        showError('A collection needs a title');
        return;
      }
      const subtitle = draftSubtitle.trim();
      setBusyId(g.id);
      try {
        const { error: rpcErr } = await supabase.rpc('admin_upsert_search_guide', {
          p_id: g.id,
          p_title: title,
          p_subtitle: subtitle || null,
          p_kind: g.kind,
          p_city_id: g.city_id,
          p_tag: g.tag,
          p_categories: g.categories,
          p_venue_ids: g.venue_ids,
          p_sort_order: g.sort_order,
          p_is_active: g.is_active,
        });
        if (rpcErr) throw new Error(rpcErr.message);
        setRows((prev) =>
          prev.map((r) => (r.id === g.id ? { ...r, title, subtitle: subtitle || null } : r)),
        );
        setEditingId(null);
        showSuccess('Renamed');
      } catch (e: unknown) {
        showError(errText(e, 'Could not rename'));
      } finally {
        setBusyId(null);
      }
    },
    [draftSubtitle, draftTitle, showError, showSuccess],
  );

  const startEdit = useCallback((g: GuideRow) => {
    setEditingId(g.id);
    setDraftTitle(g.title);
    setDraftSubtitle(g.subtitle ?? '');
  }, []);

  const onCoverPicked = useCallback(
    (guideId: string, venueId: string | null, option: CoverOption | null) => {
      setRows((prev) =>
        prev.map((r) =>
          r.id === guideId
            ? {
                ...r,
                cover_venue_id: venueId,
                cover_venue_name: option?.name ?? null,
                cover_photo_url: option?.photo_url ?? null,
                cover_in_scope: venueId != null,
                /* Optimistic only for a PIN. Clearing one hands the slot back to the automatic
                   pass, whose answer depends on every other collection — that cannot be computed
                   here, so re-list instead of guessing. */
                effective_cover_venue_id: venueId ?? r.effective_cover_venue_id,
                effective_cover_venue_name: venueId
                  ? option?.name ?? null
                  : r.effective_cover_venue_name,
                effective_cover_photo_url: venueId
                  ? option?.photo_url ?? null
                  : r.effective_cover_photo_url,
              }
            : r,
        ),
      );
      setCoverGuideId(null);
      showSuccess(venueId ? 'Cover pinned' : 'Back to the automatic cover');
      /* Unpinning frees a photo for the automatic pass, which can move OTHER collections' covers
         too. Nothing local can work that out — re-list. */
      if (!venueId) void reloadForCity(previewCityId);
    },
    [previewCityId, reloadForCity, showSuccess],
  );

  const liveCount = useMemo(() => rows.filter((r) => r.is_active).length, [rows]);
  const previewCityName = useMemo(
    () => cities.find((c) => c.id === previewCityId)?.name ?? null,
    [cities, previewCityId],
  );
  const coverGuide = useMemo(
    () => rows.find((r) => r.id === coverGuideId) ?? null,
    [coverGuideId, rows],
  );
  const detailGuide = useMemo(
    () => rows.find((r) => r.id === detailGuideId) ?? null,
    [detailGuideId, rows],
  );

  /**
   * The detail panel edits ONE row; merge its result rather than re-listing, so the table does not
   * flash and the preview city is not re-resolved. Anything the panel cannot compute locally
   * (venue counts after a pin, the automatic cover after an unpin) comes back through
   * `reloadForCity` instead — see `onOverridesChanged` on the panel below.
   */
  const patchGuide = useCallback((id: string, p: Partial<GuideRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...p } : r)));
  }, []);

  /* Live first — "what is live right now" is the question this page opens on. */
  const displayRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.title.localeCompare(b.title);
    });
  }, [rows]);

  return (
    <div className="admin-page max-w-6xl mx-auto">
      {toastNode}

      <PageHeader
        title="Collections"
        subtitle={
          <>
            {liveCount} live of {rows.length} on the search tab. Sizes and covers are shown for{' '}
            <span className="text-white">{previewCityName ?? 'no city'}</span>.
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => void load(previewCityId)}
            disabled={loading}
            className="admin-btn admin-btn-secondary admin-btn-sm"
          >
            Refresh
          </button>
        }
      />

      {error ? <ErrorBanner className="mb-5">{error}</ErrorBanner> : null}

      {setupNote && !error ? (
        <div className="mb-5 max-w-3xl rounded-md border border-border/90 bg-white/[0.025] px-3 py-2 text-[11px] leading-relaxed text-muted">
          {setupNote}
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3 gap-y-3">
        <label className="flex items-center gap-2 text-xs text-muted shrink-0">
          <span className="text-mutedDim">Preview city</span>
          <select
            value={previewCityId}
            onChange={(e) => void reloadForCity(e.target.value)}
            disabled={loading || cities.length === 0}
            className="admin-input !w-auto !px-2 !text-xs"
          >
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.pool > 0 ? ` (${c.pool})` : ''}
              </option>
            ))}
          </select>
        </label>
        <span className="rounded-full border border-border/80 bg-white/[0.04] px-3 py-1 text-[11px] text-muted">
          Collections that follow the reader are sized and covered per city — this picks which one
          you are looking at.
        </span>
      </div>

      {loading ? (
        <SkeletonRows rows={8} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No collections"
          hint="Nothing in search_guides yet. The seed collections ship in supabase/migrations/20260861000000_more_guides.sql."
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left">Collection</th>
                <th className="px-4 py-3 text-left">Kind</th>
                <th className="px-4 py-3 text-left">Scope</th>
                <th className="px-4 py-3 text-right">Venues</th>
                <th className="px-4 py-3 text-left">Cover</th>
                <th className="px-4 py-3 text-left">Live</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r, i) => {
                const editing = editingId === r.id;
                const viewerScoped = VIEWER_KINDS.has(r.kind);
                const cityScoped = !GLOBAL_KINDS.has(r.kind) && !r.city_id;
                const thumb = r.effective_cover_photo_url ?? null;
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'}`}
                  >
                    <td className="px-4 py-4 align-top">
                      {editing ? (
                        <div className="max-w-sm space-y-2">
                          <input
                            autoFocus
                            value={draftTitle}
                            onChange={(e) => setDraftTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void saveTitle(r);
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                            placeholder="Title"
                            className="admin-input"
                            aria-label={`Title for ${r.title}`}
                          />
                          <input
                            value={draftSubtitle}
                            onChange={(e) => setDraftSubtitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void saveTitle(r);
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                            placeholder="Subtitle (optional)"
                            className="admin-input"
                            aria-label={`Subtitle for ${r.title}`}
                          />
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              onClick={() => void saveTitle(r)}
                              className="admin-btn admin-btn-primary admin-btn-sm"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              onClick={() => setEditingId(null)}
                              className="admin-btn admin-btn-ghost admin-btn-sm"
                            >
                              Cancel
                            </button>
                            <span className="text-[10px] text-mutedDim">
                              {'{city}'} is replaced with the reader&apos;s city.
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="min-w-0">
                          {/* The title IS the way in — a row that opens is more discoverable than
                              a fifth link, and "expand view of a collection" is the primary job
                              this table now has. */}
                          <button
                            type="button"
                            onClick={() => setDetailGuideId(r.id)}
                            className="admin-label-primary block max-w-full truncate text-left underline-offset-2 hover:underline"
                          >
                            {r.title}
                          </button>
                          {r.subtitle ? (
                            <div className="mt-0.5 truncate text-[11px] font-normal text-mutedDim">
                              {r.subtitle}
                            </div>
                          ) : null}
                          <div className="mt-1 flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setDetailGuideId(r.id)}
                              className="text-[11px] text-muted underline-offset-2 hover:text-white hover:underline"
                            >
                              Open
                            </button>
                            {canEdit ? (
                              <button
                                type="button"
                                onClick={() => startEdit(r)}
                                className="text-[11px] text-muted underline-offset-2 hover:text-white hover:underline"
                              >
                                Rename
                              </button>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-4 align-top text-sm text-muted">
                      {KIND_LABELS[r.kind] ?? r.kind}
                      {r.tag ? <div className="text-[11px] text-mutedDim">{r.tag}</div> : null}
                    </td>

                    <td className="px-4 py-4 align-top text-sm text-muted">
                      {r.city_id ? (
                        r.city_name ?? 'One city'
                      ) : viewerScoped ? (
                        <span title="Reads the viewer's own follows — different for every user">
                          Per viewer
                        </span>
                      ) : cityScoped ? (
                        <span title="city_id is NULL on a city-scoped kind: the collection follows the reader's city">
                          Follows reader
                        </span>
                      ) : (
                        'Worldwide'
                      )}
                    </td>

                    <td className="px-4 py-4 align-top text-right admin-metric tabular-nums">
                      {viewerScoped ? (
                        <span className="text-mutedDim" title="Depends on who is looking">
                          —
                        </span>
                      ) : (
                        r.venue_count
                      )}
                      {!viewerScoped && r.is_active && (r.renders === false || r.venue_count < MIN_VENUES) ? (
                        <div
                          className="text-[10px] font-normal text-amber-400"
                          title="Under the 4-venue minimum, so the app drops this collection rather than render an empty rail"
                        >
                          not rendering
                        </div>
                      ) : null}
                    </td>

                    <td className="px-4 py-4 align-top">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border bg-black">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt=""
                              referrerPolicy="no-referrer"
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : null}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-[11px] text-muted">
                            {r.effective_cover_venue_name ?? '—'}
                          </div>
                          <div className="text-[10px] text-mutedDim">
                            {r.cover_venue_id
                              ? r.cover_in_scope
                                ? 'Pinned'
                                : 'Pinned in another city'
                              : 'Automatic'}
                          </div>
                          {canEdit ? (
                            <button
                              type="button"
                              onClick={() => setCoverGuideId(r.id)}
                              className="mt-1 text-[11px] text-muted underline-offset-2 hover:text-white hover:underline"
                            >
                              Choose
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      {canEdit ? (
                        <button
                          type="button"
                          role="switch"
                          aria-checked={r.is_active}
                          aria-label={`${r.title} live on the search tab`}
                          disabled={busyId === r.id}
                          onClick={() => void toggleLive(r, !r.is_active)}
                          className={`flex h-7 w-12 shrink-0 items-center rounded-full border px-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-accent disabled:opacity-50 ${
                            r.is_active
                              ? 'justify-end border-emerald-600/70 bg-toggleOn shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]'
                              : 'justify-start border-zinc-600/80 bg-toggleOff'
                          }`}
                        >
                          <span
                            className={`h-5 w-5 shrink-0 rounded-full shadow-md ${
                              r.is_active ? 'bg-white' : 'bg-zinc-300'
                            }`}
                          />
                        </button>
                      ) : (
                        <span
                          className={`tabular-nums text-sm font-medium ${
                            r.is_active ? 'text-emerald-400' : 'text-mutedDim'
                          }`}
                          title="Only admins can change this"
                        >
                          {r.is_active ? 'On' : 'Off'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CollectionCoverPickerModal
        open={Boolean(coverGuide)}
        guide={coverGuide}
        previewCityId={previewCityId}
        previewCityName={previewCityName}
        onClose={() => setCoverGuideId(null)}
        onPicked={onCoverPicked}
      />

      <CollectionDetailPanel
        open={Boolean(detailGuide)}
        guide={detailGuide}
        previewCityId={previewCityId}
        previewCityName={previewCityName}
        cities={cities}
        canEdit={canEdit}
        onClose={() => setDetailGuideId(null)}
        onGuidePatched={patchGuide}
        /* A pin, an exclude or a cover change moves the venue COUNT and can move OTHER
           collections' automatic covers (869 hands each one the first photo nobody has taken).
           Neither is computable here — re-list. */
        onOverridesChanged={() => void reloadForCity(previewCityId)}
      />
    </div>
  );
}
